import { CalendarClock } from 'lucide-react';
import { Tag, type TagTone } from '../ui';
import { formatIstDate } from '../lib/time';

/**
 * Days until a date, or null.
 *
 * Local midnight on BOTH sides, so "today" is 0 rather than -1 at 00:30 IST — an expiry compared
 * against `Date.now()` reads as already expired for the first half of its last day, which on a
 * warehouse screen is the difference between sellable and written off.
 */
export function daysToExpiry(d?: string | null): number | null {
  if (!d) return null;
  const t = new Date(`${String(d).slice(0, 10)}T00:00:00`).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.ceil((t - new Date().setHours(0, 0, 0, 0)) / 86_400_000);
}

/**
 * An expiry date, carrying how long is left on it.
 *
 * A DATE ALONE IS A SUBTRACTION THE READER HAS TO DO. "12 Aug 2029" and "31 Dec 2026" are equally
 * unalarming until you work out that one of them is four months away; the colour and the "· 118d"
 * say it before the date is read. Red once past, gold inside 90 days, quiet beyond — so urgency
 * stays rare and therefore legible.
 *
 * Lifted out of TransferDetailModal so the transfer FORM and the transfer DETAIL cannot disagree
 * about how close a batch is to expiring — they show the same lines, and a rule that lives in two
 * files drifts. (TransfersPage still has its own two-part variant, date text beside a separate
 * "30d left" tag, for a wider column; that one is deliberately a different shape, not a copy.)
 */
export function ExpiryChip({ date }: { date?: string | null }) {
  const d = daysToExpiry(date);
  if (!date) return <span className="type-caption text-subtle">no expiry</span>;
  const tone: TagTone = d == null ? 'neutral' : d < 0 ? 'red' : d <= 90 ? 'gold' : 'neutral';
  return (
    <Tag tone={tone} icon={<CalendarClock className="size-3" />} style={{ margin: 0 }}>
      {formatIstDate(date)}{d == null ? '' : d < 0 ? ' · expired' : d <= 120 ? ` · ${d}d` : ''}
    </Tag>
  );
}
